import { useIntl } from 'react-intl';
import { ActionExecutionResult } from '@rossum/api-client/rulesExecutionLogs';
import { Chip, Typography } from '@rossum/ui/material';
import { GridColDef } from '@rossum/ui/x-data-grid-pro';

export type LineItemRow = {
  id: string;
  lineIndex: number;
  conditionResult: boolean;
  values: Record<string, string> | null;
  actions: ActionExecutionResult[] | null;
  actionsText: string;
  hasError: boolean;
};

export const useLineItemsColumns = (): GridColDef<LineItemRow>[] => {
  const intl = useIntl();

  return [
    {
      field: 'lineIndex',
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.lineItems.columns.line',
      }),
      width: 80,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontFamily="monospace">
          {value + 1}
        </Typography>
      ),
    },
    {
      field: 'conditionResult',
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.lineItems.columns.condition',
      }),
      width: 140,
      renderCell: ({ value }) => (
        <Chip
          label={intl.formatMessage({
            id: value
              ? 'features.rulesExecutionLogs.triggerResult.true'
              : 'features.rulesExecutionLogs.triggerResult.false',
          })}
          size="small"
          color={value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'values',
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.lineItems.columns.values',
      }),
      flex: 2,
      minWidth: 200,
      renderCell: ({ value }) => {
        if (!value || Object.keys(value).length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        const display = Object.entries(value)
          .map(([key, val]) => `${key}: ${String(val)}`)
          .join(', ');
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: "'Roboto Mono Variable', monospace",
              fontSize: '0.75rem',
            }}
          >
            {display}
          </Typography>
        );
      },
    },
    {
      field: 'actionsText',
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.lineItems.columns.actions',
      }),
      flex: 2,
      minWidth: 180,
      renderCell: ({ row: lineItemRow }) => {
        if (!lineItemRow.actions || lineItemRow.actions.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({
                id: 'features.rulesExecutionLogs.lineItems.noActions',
              })}
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: "'Roboto Mono Variable', monospace",
              fontSize: '0.75rem',
            }}
          >
            {lineItemRow.actions.map(a => a.actionType).join(', ')}
          </Typography>
        );
      },
    },
    {
      field: 'hasError',
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.actions.columns.status',
      }),
      width: 120,
      renderCell: ({ row: lineItemRow }) => {
        if (!lineItemRow.actions || lineItemRow.actions.length === 0) {
          return <Typography variant="body2">—</Typography>;
        }
        const allSuccess = lineItemRow.actions.every(a => a.success);
        return (
          <Chip
            label={
              allSuccess
                ? intl.formatMessage({
                    id: 'features.rulesExecutionLogs.status.success',
                  })
                : intl.formatMessage({
                    id: 'features.rulesExecutionLogs.status.failure',
                  })
            }
            size="small"
            color={allSuccess ? 'success' : 'error'}
          />
        );
      },
    },
  ];
};
